import { useTranslation } from 'react-i18next';

/*
 * A live event's Collection Card (routes/event_cards.py): the find's picture,
 * its name -- never a rare taxon's -- where the name came from, and its
 * Research score (literature, compounds and, once confirmed, enzymes). Shown
 * in the event's chat (a `card` message) and on the event's screens. It shows
 * the card's own fields, never the record behind it.
 *
 * The score is never about eating: there is no such field to show.
 */
export default function CollectionCard({ card, compact = false, actions = null }) {
  const { t } = useTranslation('events');
  if (!card) return <div style={cs.box} data-testid="event-card" data-missing="true">{t('card.missing')}</div>;
  const name = card.rare ? t('card.rare') : card.taxon_name || t('card.unnamed');
  const source = card.taxon_source ? t(`card.source.${card.taxon_source}`) : null;
  return (
    <div style={{ ...cs.box, opacity: card.counts === false ? 0.6 : 1 }} data-testid="event-card"
      data-card={card.card_id} data-status={card.status} data-rare={card.rare ? 'true' : 'false'}>
      {card.photo_url && (
        <img src={card.photo_url} alt="" style={compact ? cs.thumbSmall : cs.thumb} loading="lazy" />
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={cs.name} dir="auto" data-testid="event-card-name">
          {card.rare ? <span aria-hidden="true">💎 </span> : null}
          <em style={{ fontStyle: card.taxon_name && !card.rare ? 'italic' : 'normal' }}>{name}</em>
        </div>
        <div style={cs.meta}>
          {card.author_name && <span>{card.author_name}</span>}
          {source && <span data-testid="event-card-source">{source}</span>}
          {card.confirmed && <span title={t('card.confirmedTip')}>✓ {t('card.confirmed')}</span>}
          {card.outline_complete && <span title={t('card.outlineTip')}>✏️</span>}
          {card.status === 'withdrawn' && <span>{t('card.withdrawn')}</span>}
        </div>
        <div style={cs.score} data-testid="event-card-score">
          {t('card.score', { score: Number(card.score || 0).toFixed(1) })}
          {card.repeat > 0 && card.counts !== false && <span style={cs.repeat}> · {t('card.repeat')}</span>}
        </div>
        {!compact && card.breakdown && Object.keys(card.breakdown).length > 0 && (
          <ul style={cs.breakdown} aria-label={t('card.breakdown')}>
            {Object.entries(card.breakdown).map(([bucket, points]) => (
              <li key={bucket}>{t(`card.bucket.${bucket}`)}: {Number(points).toFixed(1)}</li>
            ))}
          </ul>
        )}
        {actions}
      </div>
    </div>
  );
}

const cs = {
  box: { display: 'flex', gap: 10, alignItems: 'flex-start', border: '1px solid var(--c-border)', borderRadius: 12,
    padding: 10, background: 'var(--c-surface)', maxWidth: 420 },
  thumb: { width: 72, height: 72, objectFit: 'cover', borderRadius: 8, flexShrink: 0 },
  thumbSmall: { width: 48, height: 48, objectFit: 'cover', borderRadius: 6, flexShrink: 0 },
  name: { fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' },
  meta: { display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--c-text-muted)', marginTop: 2 },
  score: { marginTop: 4, fontWeight: 600, color: 'var(--c-primary)' },
  repeat: { fontWeight: 400, color: 'var(--c-text-muted)' },
  breakdown: { margin: '4px 0 0', paddingInlineStart: 18, fontSize: 12 },
};
